/**
 * Positioned event block for Day/Week time grids.
 *
 * Memoized to prevent unnecessary re-renders when sibling events change.
 *
 * @module components/calendar/EventBlock
 */

import { memo } from 'react'
import { Link } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { getStatusColor, getMeetingTitle } from '../../utils/calendarLayout'

const EventBlock = memo(
  function EventBlock({ meeting, top, height, track, totalTracks, userTz }) {
    const colors = getStatusColor(meeting.status, meeting.isDraft)
    const title = getMeetingTitle(meeting)
    const startStr = meeting.startTime || meeting.scheduledStartTime
    const showTime = height > 40

    const widthPct = totalTracks > 0 ? 100 / totalTracks : 100
    const leftPct = track * widthPct

    return (
      <Link
        to={`/meeting/${meeting.id ?? meeting.meetingId}`}
        className={`
          absolute rounded-md overflow-hidden text-xs leading-tight
          border-l-[3px] px-1.5 py-0.5
          hover:brightness-95 transition-all cursor-pointer
          ${colors.bg} ${colors.text} ${colors.border} ${colors.draftClass}
        `}
        style={{
          top,
          height: Math.max(height, 20),
          width: `calc(${widthPct}% - 2px)`,
          left: `${leftPct}%`,
        }}
        title={title}
      >
        <p className="font-medium truncate">{title}</p>
        {showTime && startStr && (
          <p className="opacity-75 truncate">
            {format(utcToZonedTime(parseISO(startStr), userTz), 'h:mm a')}
          </p>
        )}
        {meeting.isDraft && (
          <span className="text-[10px] opacity-60">Draft</span>
        )}
      </Link>
    )
  },
  (prev, next) =>
    prev.meeting.id === next.meeting.id &&
    prev.meeting.status === next.meeting.status &&
    prev.meeting.isDraft === next.meeting.isDraft &&
    prev.top === next.top &&
    prev.height === next.height &&
    prev.track === next.track &&
    prev.totalTracks === next.totalTracks
)

export default EventBlock
